import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BiLockAlt,
  BiLoaderAlt,
  BiCheckCircle,
  BiShow,
  BiHide,
} from "react-icons/bi";
import { GoBack } from "../../../components/Layout/GoBack";
import {
  resetPasswordRequest,
  clearResetPasswordSuccess,
} from "../../../redux/action";

const ResetPassword = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { token } = useParams(); // Get reset token from URL
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Using existing Redux state for reset password
  const { resetPasswordSuccess, resetPasswordError, resetPasswordLoading } =
    useSelector((state) => {
      const {
        success: { resetPassword: resetPasswordSuccess },
        errors: { resetPassword: resetPasswordError },
      } = state.ajaxStatuses || { success: {}, errors: {} };

      const { resetPasswordLoading } = state.loadingIndicator || {};

      return {
        resetPasswordSuccess,
        resetPasswordError,
        resetPasswordLoading,
      };
    });

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: yup.object({
      password: yup
        .string()
        .min(8, "Password must be at least 8 characters")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
          "Password must contain at least one uppercase letter, one lowercase letter, and one number",
        )
        .required("Password is required"),
      confirmPassword: yup
        .string()
        .oneOf([yup.ref("password"), null], "Passwords must match")
        .required("Please confirm your password"),
    }),

    onSubmit: (values) => {
      const payload = {
        formData: {
          password: values.password,
        },
        token: token,
      };
      dispatch(resetPasswordRequest(payload));
    },
  });

  useEffect(() => {
    if (resetPasswordError) {
      toast.error(resetPasswordError, {
        duration: 3000,
      });
    }
  }, [resetPasswordError]);

  useEffect(() => {
    if (resetPasswordSuccess) {
      setIsSubmitted(true);
      toast.success("Password reset successfully!", {
        duration: 4000,
      });
      dispatch(clearResetPasswordSuccess());
    }
  }, [resetPasswordSuccess, dispatch]);

  // Check if token exists
  useEffect(() => {
    if (!token) {
      toast.error("Invalid reset link. Please request a new password reset.", {
        duration: 4000,
      });
      history.push("/forgot-password");
    }
  }, [token, history]);

  if (isSubmitted) {
    return (
      <div className="min-h-screen w-full relative flex items-center justify-center bg-slate-50 overflow-hidden font-body selection:bg-blue-100">
        {/* --- Ambient Background "Glam" (Light Mode) --- */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-200/40 rounded-full blur-[100px] pointer-events-none -translate-x-1/3 -translate-y-1/3 mix-blend-multiply"></div>
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-purple-200/40 rounded-full blur-[100px] pointer-events-none translate-x-1/3 translate-y-1/3 mix-blend-multiply"></div>

        {/* --- Success Card --- */}
        <div className="relative z-10 w-full max-w-[420px] px-6">
          <GoBack />

          <div className="bg-white/60 backdrop-blur-xl border border-white/80 rounded-[2rem] p-8 md:p-10 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] relative overflow-hidden">
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-80"></div>

            {/* Success Content */}
            <div className="text-center">
              <div className="mb-6">
                <BiCheckCircle
                  className="mx-auto text-green-500 mb-4"
                  size={64}
                />
                <h1 className="font-head text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mb-2">
                  Password reset successful!
                </h1>
                <p className="text-slate-500 text-sm">
                  Your password has been successfully updated. You can now sign
                  in with your new password.
                </p>
              </div>

              <div className="space-y-4">
                <Link to="/signin">
                  <button className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm rounded-xl shadow-lg shadow-blue-600/30 hover:shadow-blue-600/40 transform active:scale-[0.98] transition-all">
                    Continue to Sign In
                  </button>
                </Link>
              </div>
            </div>
          </div>

          {/* Footer Security Seal */}
          <div className="mt-8 text-center">
            <p className="text-[10px] text-slate-400 font-medium uppercase tracking-widest flex items-center justify-center gap-2">
              <BiLockAlt size={12} /> Secure 256-bit Encryption
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full relative flex items-center justify-center bg-slate-50 overflow-hidden font-body selection:bg-blue-100">
      {/* --- Ambient Background "Glam" (Light Mode) --- */}
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-200/40 rounded-full blur-[100px] pointer-events-none -translate-x-1/3 -translate-y-1/3 mix-blend-multiply"></div>
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-purple-200/40 rounded-full blur-[100px] pointer-events-none translate-x-1/3 translate-y-1/3 mix-blend-multiply"></div>

      {/* --- Main Glass Card --- */}
      <div className="relative z-10 w-full max-w-[420px] px-6">
        <GoBack />

        <div className="bg-white/60 backdrop-blur-xl border border-white/80 rounded-[2rem] p-8 md:p-10 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] relative overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-80"></div>

          {/* Header */}
          <div className="flex flex-col gap-y-2 mb-8 text-center">
            <h1 className="font-head text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
              Set new password
            </h1>
            <p className="text-slate-500 text-xs md:text-sm">
              Your new password must be different from previous used passwords.
            </p>
          </div>

          <form
            onSubmit={formik?.handleSubmit}
            className="flex flex-col gap-y-5"
          >
            {/* Password Field */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                New Password
              </label>
              <div className="relative group">
                <div className="absolute left-4 top-3.5 text-slate-400 group-focus-within:text-blue-600 transition-colors">
                  <BiLockAlt size={20} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  {...formik?.getFieldProps("password")}
                  placeholder="Enter your new password"
                  className="w-full bg-white/70 border border-slate-200/80 text-slate-800 placeholder-slate-400 text-sm rounded-xl py-3.5 pl-11 pr-11 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all hover:bg-white hover:border-slate-300"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-3.5 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <BiHide size={20} /> : <BiShow size={20} />}
                </button>
              </div>
              {formik?.errors?.password && formik?.touched?.password && (
                <p className="text-red-500 text-xs ml-1 font-medium">
                  {formik.errors.password}
                </p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                Confirm Password
              </label>
              <div className="relative group">
                <div className="absolute left-4 top-3.5 text-slate-400 group-focus-within:text-blue-600 transition-colors">
                  <BiLockAlt size={20} />
                </div>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  {...formik?.getFieldProps("confirmPassword")}
                  placeholder="Confirm your new password"
                  className="w-full bg-white/70 border border-slate-200/80 text-slate-800 placeholder-slate-400 text-sm rounded-xl py-3.5 pl-11 pr-11 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all hover:bg-white hover:border-slate-300"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-3.5 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showConfirmPassword ? (
                    <BiHide size={20} />
                  ) : (
                    <BiShow size={20} />
                  )}
                </button>
              </div>
              {formik?.errors?.confirmPassword &&
                formik?.touched?.confirmPassword && (
                  <p className="text-red-500 text-xs ml-1 font-medium">
                    {formik.errors.confirmPassword}
                  </p>
                )}
            </div>

            {/* Password Requirements */}
            <div className="bg-slate-50/50 rounded-lg p-3 text-xs text-slate-600">
              <p className="font-semibold mb-1">Password must contain:</p>
              <ul className="space-y-0.5 text-slate-500">
                <li>• At least 8 characters</li>
                <li>• One uppercase letter</li>
                <li>• One lowercase letter</li>
                <li>• One number</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="pt-4 flex flex-col gap-y-6">
              <button
                type="submit"
                disabled={resetPasswordLoading}
                className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm rounded-xl shadow-lg shadow-blue-600/30 hover:shadow-blue-600/40 transform active:scale-[0.98] transition-all disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center gap-2"
              >
                {resetPasswordLoading ? (
                  <>
                    <BiLoaderAlt className="animate-spin" size={20} />
                    <span>Updating...</span>
                  </>
                ) : (
                  "Update password"
                )}
              </button>

              <div className="text-center">
                <Link to="/signin">
                  <span className="text-sm text-slate-500">
                    <span className="text-blue-600 font-bold hover:text-blue-700 transition-colors cursor-pointer">
                      ← Back to sign in
                    </span>
                  </span>
                </Link>
              </div>
            </div>
          </form>
        </div>

        {/* Footer Security Seal */}
        <div className="mt-8 text-center">
          <p className="text-[10px] text-slate-400 font-medium uppercase tracking-widest flex items-center justify-center gap-2">
            <BiLockAlt size={12} /> Secure 256-bit Encryption
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
